import React from 'react';

import { Box, Typography, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { AlertTriangleIcon } from 'lucide-react';

interface AuthErrorMessageProps {
  // VERA-837: ReactNode (not just string) so a message can carry an inline
  // action, e.g. "...already exists. Sign in instead." with the CTA as a
  // real clickable link rather than plain text.
  message: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Inline error surface for auth screens (sign in, password, claim, etc).
 *
 * NOTE: this deliberately uses plain MUI Box/Typography rather than the
 * `@repo/ui` Alert. apps/app renders inside a shadow DOM with its own emotion
 * cache, and the `@repo/ui` Alert resolves to `display: none` in that context
 * (its styled() emotion styles are not injected into the shadow root), so the
 * message would be present in the DOM but invisible. Box/Typography + a lucide
 * icon render reliably here. TODO(VRG-173): once auth components are promoted
 * into the shared library, fix the Alert-in-shadow-DOM rendering instead.
 */
const ERROR_COLOR = '#b91c1c';

export const AuthErrorMessage = ({ message, sx }: AuthErrorMessageProps) => {
  if (!message) return null;

  return (
    <Box
      role='alert'
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        px: 1.5,
        py: 1.25,
        // VERA-837: same radius token as fields/buttons. Note `borderRadius: 2`
        // in sx is a MULTIPLIER of theme.shape.borderRadius, so it rendered at
        // 2x (16px) against the 8px buttons. Use the token's px value directly.
        borderRadius: `${theme.shape.borderRadius}px`,
        // VERA-842: the border must be the ALERT's own colour. It rendered grey
        // next to red text because index.css's `#vera-shadow-root *` border-color
        // rule has ID specificity and beats any emotion class rule. `!important`
        // is what wins, not the property form — a separate `borderColor` without
        // it loses just as a shorthand does. Colour matches the text/icon so the
        // block reads as one status. (Previously blamed on Tailwind preflight,
        // which is disabled.)
        borderStyle: 'solid',
        borderWidth: '1px',
        // !important because index.css's `#vera-shadow-root *` border-color rule
        // has ID specificity and keeps winning the cascade here; measured grey
        // next to red text without it. (Not "preflight" — that is disabled.)
        // Subtle: the status colour at low alpha, so the border reads as the
        // same status without competing with the message text for attention.
        borderColor: `${alpha(ERROR_COLOR, 0.28)} !important`,
        backgroundColor: 'rgba(220, 38, 38, 0.06)',
        ...sx,
      })}
    >
      <AlertTriangleIcon
        size={18}
        strokeWidth={1.75}
        style={{ color: ERROR_COLOR, flexShrink: 0 }}
      />
      <Typography variant='body2' sx={{ color: ERROR_COLOR, lineHeight: 1.4 }}>
        {message}
      </Typography>
    </Box>
  );
};
